/**
 * Audio Preloader Service
 *
 * Buffers one sound the visitor is about to play (hovering or pressing its
 * play control, see hooks/useIntentPreload.ts) so playback starts without a
 * second request. It must never be driven by visibility: every preload is a
 * full WAV download from Supabase Storage, which bills cached egress per
 * byte, and a scrolled list used to buffer every row it passed.
 *
 * At most one load is in flight — starting another cancels the one before
 * it — and at most three buffered elements are kept (LRU).
 */

const isDev = process.env.NODE_ENV === 'development';

interface PreloadedAudio {
  element: HTMLAudioElement;
  url: string;
  ready: boolean;
  timestamp: number;
}

class AudioPreloader {
  private static instance: AudioPreloader;
  private preloadedAudio: Map<string, PreloadedAudio> = new Map();
  private loadingPromises: Map<string, Promise<void>> = new Map();
  private maxPreloaded = 3;

  private constructor() {}

  static getInstance(): AudioPreloader {
    if (!AudioPreloader.instance) {
      AudioPreloader.instance = new AudioPreloader();
    }
    return AudioPreloader.instance;
  }

  /**
   * Preload audio data for a sound. Returns immediately if already preloaded/loading.
   */
  async preload(soundId: string, url: string): Promise<void> {
    // Skip if already preloaded with same URL
    const existing = this.preloadedAudio.get(soundId);
    if (existing && existing.url === url && existing.ready) {
      return;
    }

    // Skip if already loading
    if (this.loadingPromises.has(soundId)) {
      return this.loadingPromises.get(soundId);
    }

    const loadPromise = this.doPreload(soundId, url);
    this.loadingPromises.set(soundId, loadPromise);

    try {
      await loadPromise;
    } finally {
      this.loadingPromises.delete(soundId);
    }
  }

  private async doPreload(soundId: string, url: string): Promise<void> {
    this.cancelInFlight(soundId);
    // Evict oldest if at capacity
    this.evictIfNeeded();

    if (isDev) console.log(`[AudioPreloader] Preloading: ${soundId}`);

    const audio = new Audio();
    // Matches hooks/useAudioPlayer.ts: Supabase Storage serves
    // `access-control-allow-origin: *` and /api/audio-proxy mirrors the
    // caller's origin, so 'anonymous' mode loads cleanly for every URL this
    // preloader is given — and without it, a preloaded element handed to
    // useAudioLevel's analyser would read as permanently silent (tainted).
    audio.crossOrigin = 'anonymous';
    audio.preload = 'auto';

    // Store immediately (not ready yet)
    this.preloadedAudio.set(soundId, {
      element: audio,
      url,
      ready: false,
      timestamp: Date.now(),
    });

    // Set source and wait for enough data
    return new Promise<void>((resolve) => {
      const cleanup = () => {
        audio.removeEventListener('canplaythrough', onReady);
        audio.removeEventListener('error', onError);
        clearTimeout(timeoutId);
      };

      // A cancelled or handed-over element can still fire; only touch the
      // entry that owns this element, never a newer one for the same sound.
      const ownEntry = () => {
        const entry = this.preloadedAudio.get(soundId);
        return entry?.element === audio ? entry : undefined;
      };

      const onReady = () => {
        cleanup();
        const entry = ownEntry();
        if (entry) {
          entry.ready = true;
          if (isDev) console.log(`[AudioPreloader] Ready: ${soundId}`);
        }
        resolve();
      };

      const onError = () => {
        cleanup();
        // Remove failed entry
        if (ownEntry()) this.preloadedAudio.delete(soundId);
        if (isDev) console.log(`[AudioPreloader] Failed: ${soundId}`);
        resolve(); // Don't reject - just skip this preload
      };

      // Timeout after 8 seconds (don't block forever)
      const timeoutId = setTimeout(() => {
        cleanup();
        // Keep the entry even if not fully ready - partial buffer is still useful
        const entry = ownEntry();
        if (entry && audio.readyState >= 2) {
          entry.ready = true;
          if (isDev) console.log(`[AudioPreloader] Partial ready: ${soundId}`);
        }
        resolve();
      }, 8000);

      audio.addEventListener('canplaythrough', onReady, { once: true });
      audio.addEventListener('error', onError, { once: true });
      audio.src = url;
      audio.load();
    });
  }

  /**
   * Get a preloaded audio element, ready or still loading. Transfers
   * ownership - caller must manage the element. Handing over an element that
   * is mid-load lets playback continue the download already under way rather
   * than start a second one for the same file.
   * Returns null if this sound was never preloaded (or was evicted/cancelled).
   */
  getPreloaded(soundId: string): HTMLAudioElement | null {
    const entry = this.preloadedAudio.get(soundId);
    if (!entry) {
      return null;
    }

    // Transfer ownership - remove from cache
    this.preloadedAudio.delete(soundId);
    this.loadingPromises.delete(soundId);
    if (isDev) console.log(`[AudioPreloader] Transferred: ${soundId}`);
    return entry.element;
  }

  /**
   * Check if a sound is preloaded and ready.
   */
  isReady(soundId: string): boolean {
    const entry = this.preloadedAudio.get(soundId);
    return entry?.ready ?? false;
  }

  /**
   * Cancel preloading for a sound (e.g., when card scrolls out of view).
   */
  cancel(soundId: string): void {
    const entry = this.preloadedAudio.get(soundId);
    if (entry) {
      entry.element.src = '';
      entry.element.load();
      this.preloadedAudio.delete(soundId);
    }
    this.loadingPromises.delete(soundId);
  }

  /**
   * Clear all preloaded audio (useful for cleanup).
   */
  clear(): void {
    for (const [, entry] of this.preloadedAudio) {
      entry.element.src = '';
      entry.element.load();
    }
    this.preloadedAudio.clear();
    this.loadingPromises.clear();
  }

  private cancelInFlight(exceptId: string): void {
    for (const [id, entry] of this.preloadedAudio) {
      if (id !== exceptId && !entry.ready) this.cancel(id);
    }
  }

  private evictIfNeeded(): void {
    if (this.preloadedAudio.size < this.maxPreloaded) {
      return;
    }

    // Find oldest entry
    let oldestId: string | null = null;
    let oldestTimestamp = Infinity;

    for (const [id, entry] of this.preloadedAudio) {
      if (entry.timestamp < oldestTimestamp) {
        oldestTimestamp = entry.timestamp;
        oldestId = id;
      }
    }

    if (oldestId) {
      const entry = this.preloadedAudio.get(oldestId);
      if (entry) {
        entry.element.src = '';
        entry.element.load();
      }
      this.preloadedAudio.delete(oldestId);
      if (isDev) console.log(`[AudioPreloader] Evicted: ${oldestId}`);
    }
  }
}

// Export singleton instance
export const audioPreloader = AudioPreloader.getInstance();
